import { FC, useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { CircularProgress } from '@mui/material';
import OTPInput from 'react-otp-input';
import { T_KEYS } from '../../constants';

interface GathernOTPStepProps {
  phoneNumber: string;
  onSubmit: (otp: string) => void;
  onResend: () => void;
  isLoading: boolean;
  error?: string;
}

const OTP_LENGTH = 4;
const RESEND_COOLDOWN = 30; // seconds

const GathernOTPStep: FC<GathernOTPStepProps> = ({
  phoneNumber,
  onSubmit,
  onResend,
  isLoading,
  error,
}) => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';
  const [otp, setOtp] = useState('');
  const [resendTimer, setResendTimer] = useState(RESEND_COOLDOWN);

  // Format phone number for display
  const formatPhoneNumber = (phone: string) => {
    // Remove +966 prefix and format as (50)444-4565
    const cleaned = phone.replace('+966', '');
    if (cleaned.length >= 9) {
      return `+966(${cleaned.slice(0, 2)})${cleaned.slice(2, 5)}-${cleaned.slice(5)}`;
    }
    return phone;
  };

  // Resend timer countdown
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  // Check if OTP is complete
  const isOtpComplete = otp.length === OTP_LENGTH;

  // Handle OTP change
  const handleOtpChange = useCallback((value: string) => {
    setOtp(value);
  }, []);

  // Handle submit
  const handleSubmit = useCallback(() => {
    if (isOtpComplete && !isLoading) {
      onSubmit(otp);
    }
  }, [isOtpComplete, isLoading, otp, onSubmit]);

  // Handle resend
  const handleResend = useCallback(() => {
    if (resendTimer === 0) {
      onResend();
      setResendTimer(RESEND_COOLDOWN);
      setOtp('');
    }
  }, [resendTimer, onResend]);

  return (
    <Container>
      <Header>
        <Title>{t(T_KEYS.VERIFY_NUMBER_TITLE)}</Title>
        <Description>
          {t(T_KEYS.VERIFY_NUMBER_DESC, { phone: formatPhoneNumber(phoneNumber) })}
        </Description>
      </Header>

      <OTPSection>
        <OTPInputWrapper $hasError={!!error}>
          <OTPInput
            value={otp}
            onChange={handleOtpChange}
            numInputs={OTP_LENGTH}
            shouldAutoFocus
            inputType="tel"
            skipDefaultStyles
            containerStyle="otp-container"
            inputStyle="otp-input"
            renderInput={(props) => <input {...props} disabled={isLoading} />}
          />
        </OTPInputWrapper>

        {error && <ErrorText>{error}</ErrorText>}

        <ResendText>
          {t(T_KEYS.DIDNT_RECEIVE_CODE)}
          {resendTimer > 0 ? (
            <span>
              {t(T_KEYS.RESEND_IN, { seconds: resendTimer })}
            </span>
          ) : (
            <ResendButton onClick={handleResend}>
              {t(T_KEYS.RESEND)}
            </ResendButton>
          )}
        </ResendText>
      </OTPSection>

      <SubmitButton 
        onClick={handleSubmit}
        disabled={!isOtpComplete || isLoading}
        $disabled={!isOtpComplete || isLoading}
      >
        {isLoading ? (
          <CircularProgress size={20} sx={{ color: 'white' }} />
        ) : (
          t(T_KEYS.CONTINUE)
        )}
      </SubmitButton>
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  background: white;
  border-radius: 24px;
  padding: 40px 32px;
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Title = styled.h2`
  font-weight: 700;
  font-size: 28px;
  line-height: 36px;
  color: #262626;
  margin: 0;
`;

const Description = styled.p`
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #666666;
  margin: 0;
`;

const OTPSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const OTPInputWrapper = styled.div<{ $hasError?: boolean }>`
  display: flex;
  justify-content: center;
  direction: ltr;
  
  .otp-container {
    display: flex;
    justify-content: center;
    gap: 10px;
  }
  
  .otp-input {
    width: 84px !important;
    height: 84px !important;
    background: #f8f8f8;
    border: 1px solid ${({ $hasError }) => $hasError ? '#d32f2f' : '#eaeaea'};
    border-radius: 16px;
    font-weight: 600;
    font-size: 24px;
    text-align: center;
    color: #262626;
    outline: none;
    transition: all 0.2s ease;
    
    &:focus {
      border-color: ${({ $hasError }) => $hasError ? '#d32f2f' : '#5b2ff9'};
      background: white;
    }
    
    &:disabled {
      opacity: 0.6;
    }
    
    &::placeholder {
      color: #bdbdbd;
    }
  }
`;

const ErrorText = styled.span`
  font-size: 14px;
  line-height: 20px;
  color: #d32f2f;
`;

const ResendText = styled.p`
  font-size: 14px;
  line-height: 20px;
  color: #262626;
  margin: 0;

  span {
    color: #666666;
  }
`;

const ResendButton = styled.button`
  background: none;
  border: none;
  color: #5b2ff9;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`;

const SubmitButton = styled.button<{ $disabled?: boolean }>`
  width: 100%;
  height: 56px;
  background: ${({ $disabled }) => $disabled ? '#dddddd' : '#5b2ff9'};
  color: ${({ $disabled }) => $disabled ? '#bdbdbd' : 'white'};
  border: none;
  border-radius: 16px;
  font-weight: 600;
  font-size: 18px;
  line-height: 56px;
  cursor: ${({ $disabled }) => $disabled ? 'not-allowed' : 'pointer'};
  transition: background 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background: ${({ $disabled }) => $disabled ? '#dddddd' : '#4a25d6'};
  }
`;

export default GathernOTPStep;
